import React from "react";
import useSWR from "swr";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, Stat, Spinner, KV, Section, ProgressBar } from "@/components/Atoms";
import { fmtRel } from "@/lib/format";
import { Server, Wrench, BellRing, Download } from "lucide-react";
import { HealthBanner, ServiceHealthPanel } from "@/components/ServiceHealth";
import StorageConsistencyOverview from "@/components/StorageConsistencyOverview";

const fetcher = (u) => api.get(u).then((r) => r.data);

export default function OverviewPage() {
    const { data: stats, isLoading } = useSWR("/api/admin/stats", fetcher, {
        refreshInterval: 4000,
    });
    const { data: jobs } = useSWR("/api/admin/jobs?limit=8", fetcher, {
        refreshInterval: 3000,
    });
    const { data: sysinfo } = useSWR("/api/admin/system/info", fetcher, {
        refreshInterval: 30000,
    });

    const { data: health } = useSWR("/api/admin/infra/health", fetcher, {
        refreshInterval: 15000,
    });

    if (isLoading || !stats)
        return (
            <div className="p-8">
                <Spinner />
            </div>
        );

    const hSummary = health?.summary || {};
    const hCounts = hSummary.counts || {};
    const running = stats.jobs?.running || 0;
    const queued = stats.jobs?.queued || 0;
    const runningJobs = (jobs || []).filter((j) => j.status === "running").slice(0, 8);

    return (
        <div data-testid="overview-page">
            <PageHeader
                testid="overview-header"
                title="Overview"
                subtitle="Monitoring · Dashboard"
            />
            <div className="p-4 sm:p-6 space-y-4">
                {sysinfo && <ProviderBanner sysinfo={sysinfo} />}
                <HealthBanner />

                {/* Monitoring summary cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-cm-line">
                    <Stat
                        testid="stat-critical"
                        label="Critical / Down"
                        value={(hCounts.critical || 0) + (hCounts.down || 0)}
                        valueCls={(hCounts.critical || 0) + (hCounts.down || 0) > 0 ? "text-cm-danger" : "text-cm-text"}
                        sub={(hCounts.critical || 0) + (hCounts.down || 0) > 0 ? (
                            <span className="text-cm-danger font-mono">needs attention</span>
                        ) : "all clear"}
                    />
                    <Stat
                        testid="stat-degraded"
                        label="Degraded"
                        value={hCounts.degraded || 0}
                        valueCls={hCounts.degraded > 0 ? "text-cm-warning" : "text-cm-text"}
                        sub={hCounts.degraded > 0 ? "slow / partial" : "none"}
                    />
                    <Stat
                        testid="stat-healthy"
                        label="Healthy"
                        value={hCounts.healthy || 0}
                        valueCls="text-cm-success"
                        sub={`${hCounts.pending || 0} pending`}
                    />
                    <Stat
                        testid="stat-agents"
                        label="Agents"
                        value={`${stats.agents_online} / ${stats.agents_total}`}
                        sub="online / total"
                    />
                    <Stat
                        testid="stat-jobs"
                        label="Jobs"
                        value={running + queued}
                        valueCls={(running + queued) > 5 ? "text-cm-warning" : "text-cm-text"}
                        sub={`running:${running} queued:${queued}`}
                    />
                </div>

                {/* Full-width service health — PRIMARY focus */}
                <ServiceHealthPanel />

                {/* Secondary: running jobs + quick stats */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <Section
                        className="lg:col-span-2"
                        testid="overview-jobs-card"
                        title="Running jobs"
                        icon={Wrench}
                        actions={
                            <Link
                                to="/jobs"
                                className="text-xs text-cm-brand hover:text-cm-brandDim font-mono uppercase tracking-wider"
                                data-testid="overview-jobs-link"
                            >
                                all jobs →
                            </Link>
                        }
                        bodyCls="p-0"
                    >
                        <div className="cm-table-wrap">
                        <table className="cm-table">
                            <thead>
                                <tr>
                                    <th className="cm-th">Type</th>
                                    <th className="cm-th">Status</th>
                                    <th className="cm-th">Progress</th>
                                    <th className="cm-th">When</th>
                                </tr>
                            </thead>
                            <tbody>
                                {runningJobs.map((j) => (
                                    <tr key={j.id} className="cm-tr" data-testid={`overview-job-row-${j.id}`}>
                                        <td className="cm-td font-mono text-xs truncate max-w-[160px]">{j.type}</td>
                                        <td className="cm-td">
                                            <span className={`cm-badge cm-badge-${badgeFor(j.status)}`}>
                                                {j.status}
                                            </span>
                                        </td>
                                        <td className="cm-td font-mono text-xs">
                                            <ProgressBar value={j.progress} />
                                        </td>
                                        <td className="cm-td text-xs text-cm-muted">
                                            {fmtRel(j.created_at)}
                                        </td>
                                    </tr>
                                ))}
                                {runningJobs.length === 0 && (
                                    <tr><td colSpan="4" className="cm-td text-xs text-cm-muted text-center py-4">No running jobs</td></tr>
                                )}
                            </tbody>
                        </table>
                        </div>
                    </Section>

                    <Section testid="overview-quickstats" title="Quick stats" icon={Server} bodyCls="px-4 pb-2">
                        <KV k="Clouds" v={<span className="font-mono">{stats.clouds} <span className="text-cm-muted">({stats.clouds_by_status.active} active)</span></span>} />
                        <KV k="Resellers" v={<span className="font-mono">{stats.resellers} <span className="text-cm-muted">({stats.resellers_active} active)</span></span>} />
                        <KV k="Service VMs" v={<span className="font-mono">{stats.service_vms?.total || "—"}</span>} />
                        <KV k="Domains" v={<span className="font-mono">{stats.domains}</span>} />
                        <KV k="Alerts (24h)" v={<span className="font-mono">{stats.monitoring?.alerts_recent || 0}</span>} />
                        <StorageConsistencyOverview />
                    </Section>

                    <Section
                        className="lg:col-span-3"
                        testid="overview-oncall"
                        eyebrow="On-call"
                        title="Incident response"
                        icon={BellRing}
                        actions={
                            <Link to="/alerts" className="text-xs text-cm-brand hover:text-cm-brandDim font-mono uppercase tracking-wider">
                                Alert Center →
                            </Link>
                        }
                    >
                        <OnCallWidget />
                    </Section>
                </div>
            </div>
        </div>
    );
}

function OnCallWidget() {
    const { data } = useSWR("/api/admin/infra/oncall/now", fetcher, { refreshInterval: 30000 });
    const { data: open } = useSWR("/api/admin/infra/monitoring/open-alerts", fetcher, { refreshInterval: 15000 });
    const { data: report } = useSWR("/api/admin/infra/sla/report?days=30", fetcher, { refreshInterval: 300000 });
    const names = (data?.employees || []).map((e) => e.name).join(", ");
    const violating = (report?.clouds || []).filter((c) => c.within_sla === false);
    const escalated = ((open || []).filter((a) => a.escalated_at).length > 0);
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-cm-line">
                <div className="cm-card px-4 py-3">
                    <div className="cm-label">On-call now</div>
                    <div className="font-mono text-sm text-cm-text mt-1">{names || "no technicians configured"}</div>
                </div>
                <div className="cm-card px-4 py-3">
                    <div className="cm-label">Open alerts</div>
                    <Link to="/alerts" className={`font-mono text-sm mt-1 block ${(open?.length || 0) > 0 ? "text-cm-danger" : "text-cm-success"}`}>
                        {(open || []).length} {((open || []).length) === 1 ? "alert" : "alerts"}
                    </Link>
                </div>
                <div className="cm-card px-4 py-3">
                    <div className="cm-label">Escalation</div>
                    <div className={`font-mono text-sm mt-1 ${escalated ? "text-cm-danger" : "text-cm-muted"}`}>
                        {escalated ? "active" : "quiet"}
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-cm-line">
                <div className="cm-card px-4 py-3">
                    <div className="cm-label">SLA violations (30d)</div>
                    <Link to="/alerts" className={`font-mono text-sm mt-1 block ${violating.length ? "text-cm-danger" : "text-cm-success"}`}>
                        {violating.length} {violating.length === 1 ? "cloud" : "clouds"}
                    </Link>
                </div>
                <div className="cm-card px-4 py-3 flex flex-col">
                    <div className="cm-label">SLA report</div>
                    <a href="/api/admin/infra/sla/report.csv?days=30" className="font-mono text-sm text-cm-brand mt-1 flex items-center gap-1.5" target="_blank" rel="noreferrer">
                        <Download size={12} /> CSV
                    </a>
                </div>
            </div>
        </div>
    );
}

function badgeFor(s) {
    return (
        {
            active: "success",
            succeeded: "success",
            online: "success",
            running: "info",
            provisioning: "info",
            updating: "info",
            queued: "warning",
            pending: "warning",
            enrolled: "warning",
            suspended: "neutral",
            offline: "neutral",
            canceled: "neutral",
            failed: "danger",
            error: "danger",
            deleting: "danger",
        }[s] || "neutral"
    );
}

function ProviderBanner({ sysinfo }) {
    const providers = sysinfo.providers || {};
    const entries = Object.entries(providers);
    const allLive = entries.every(([, v]) => v.mode === "live");
    const anyLive = entries.some(([, v]) => v.mode === "live");
    const tone = allLive
        ? "border-cm-success/40 bg-cm-success/5 text-cm-success"
        : anyLive
        ? "border-cm-warning/40 bg-cm-warning/5 text-cm-warning"
        : "border-cm-line bg-cm-surface text-cm-muted";
    return (
        <div
            data-testid="provider-banner"
            className={`border ${tone} px-4 py-2 flex flex-wrap items-center gap-3 font-mono text-xs uppercase tracking-widest`}
        >
            <span className="text-cm-dim">Provider:</span>
            {entries.map(([k, v]) => (
                <span
                    key={k}
                    data-testid={`provider-${k}-mode`}
                    className={
                        v.mode === "live"
                            ? "text-cm-success"
                            : "text-cm-dim"
                    }
                    title={Object.entries(v)
                        .filter(([kk]) => kk !== "mode")
                        .map(([kk, vv]) => `${kk}=${vv ?? "—"}`)
                        .join("  ")}
                >
                    {k}:<span className="ml-1">{v.mode}</span>
                </span>
            ))}
            {sysinfo.public_base_url && (
                <span className="ml-auto text-cm-dim normal-case tracking-normal">
                    <span className="text-cm-dim">cm:</span> {sysinfo.public_base_url}
                </span>
            )}
        </div>
    );
}
